import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';
import { CssBaseline } from 'src/components/muiTheme/utils';

// eslint-disable-next-line no-restricted-imports
import type {} from '@mui/material/themeCssVarsAugmentation';
import { EnhancedRouter } from './components/EnhancedRouter';
import { Toaster } from './components/Toaster';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { NativeBridgeProvider } from './contexts/NativeBridgeContext';
import {
  SessionContextProvider,
  useOptionalSession,
} from './contexts/SessionContext';
import { ThemeProvider } from './contexts/ThemeProvider';
import { useIntercomProps } from './hooks/useIntercomAppId';
import { usePostMessages } from './hooks/usePostMessages';
import Routes from './routes/Routes';
import { ErrorBoundary } from './shared/ErrorBoundary';
import ForceResetPasswordDialog from './shared/ForceResetPasswordDialog';

import './index.css';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 5000 },
  },
});

const IntercomWrapper = ({ children }) => {
  const { appId, userId, userHash } = useIntercomProps();

  return (
    <IntercomProvider
      appId={appId || ''}
      autoBoot
      shouldInitialize={!!appId}
      autoBootProps={{
        userId,
        userHash,
      }}
    >
      {children}
    </IntercomProvider>
  );
};

const AppContent = () => {
  const { user, refreshUser } = useOptionalSession();

  return (
    <IntercomWrapper>
      <AnalyticsProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider>
            <ErrorBoundary>
              <Routes />
              {user?.forceResetPassword && (
                <ForceResetPasswordDialog onSuccess={refreshUser} />
              )}
              <Toaster />
              <CssBaseline />
            </ErrorBoundary>
          </ThemeProvider>
        </LocalizationProvider>
      </AnalyticsProvider>
    </IntercomWrapper>
  );
};

export function App() {
  usePostMessages();

  LicenseInfo.setLicenseKey(
    '5a9a7e16311c662d12497fbdf5aba0d4Tz0xMDE3NjAsRT0xNzYzMTM4MzU5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI='
  );

  return (
    <QueryClientProvider client={queryClient}>
      <EnhancedRouter>
        <ScrollToTopOnNavigate />
        <NativeBridgeProvider>
          <SessionContextProvider>
            <AppContent />
          </SessionContextProvider>
        </NativeBridgeProvider>
      </EnhancedRouter>
    </QueryClientProvider>
  );
}
export const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};
